import React from "react";
import { config } from "../../../constants";
import { Player } from "../../../types";
import { GameStatus } from "../../../types/game-status";
import { useAppSelector } from "../../../redux/store";
import crown2 from "../../../images/crown2.png";

type Size = "small" | "medium" | "large" | "lobby";
type Variant = "basic" | "profile" | "lobby";

interface BaseProps {
  spriteId: number;
  name?: string;
  size?: Size;
  variant?: Variant;
  noAnimate?: boolean;
  special?: boolean;
}

interface ProfileProps extends BaseProps {
  variant: "profile";
  bigText?: boolean;
  premium?: boolean;
  onClick?: () => void;
}

interface LobbyProps extends BaseProps {
  variant: "lobby";
  player?: Player;
  closed?: boolean;
  checked?: boolean;
  onClick?: (playerId: number) => void;
}

interface BasicProps extends BaseProps {
  variant?: "basic";
  onClick?: () => void;
}

type AvatarProps = BasicProps | ProfileProps | LobbyProps;

const Avatar = React.memo((props: AvatarProps) => {
  const {
    spriteId,
    name,
    size = "medium",
    variant = "basic",
    noAnimate,
    special,
  } = props;

  // Get avatar paths based on size and special status
  const getAvatarPath = () => {
    const paths = special ? config.avatarsSpecial : getPathsForSize();
    return paths[spriteId];
  };

  const getPathsForSize = () => {
    switch (size) {
      case "small":
      case "lobby":
        return config.avatarsSmall;
      case "medium":
        return config.avatars192;
      case "large":
        return config.avatars;
      default:
        return config.avatars192;
    }
  };

  const getResponsiveAvatarPaths = () => {
    if (special) {
      return {
        xsmall: config.avatarsSpecial[spriteId],
        small: config.avatarsSpecial[spriteId],
        medium: config.avatarsSpecial[spriteId],
        large: config.avatarsSpecial[spriteId],
      };
    }

    return {
      xsmall: config.avatarsXSmall[spriteId],
      small: config.avatarsSmall[spriteId],
      medium: config.avatars192[spriteId],
      large: config.avatars[spriteId],
    };
  };

  // Lobby-specific logic
  const gameState = useAppSelector((state) => state.game);
  const playerState = useAppSelector((state) => state.player);

  const amHost = () => {
    return gameState.game?.host_id === playerState.player?.id;
  };

  const handleLobbyClick = () => {
    if (variant === "lobby" && "player" in props && props.player && amHost()) {
      if (props.onClick) {
        props.onClick(props.player.id);
      }
    }
  };

  const handleBasicClick = () => {
    if (variant === "basic" && "onClick" in props && props.onClick) {
      (props as BasicProps).onClick?.();
    } else if (variant === "profile" && "onClick" in props && props.onClick) {
      (props as ProfileProps).onClick?.();
    }
  };

  const isChecked = () => {
    return (
      variant === "lobby" &&
      "checked" in props &&
      "player" in props &&
      props.player !== undefined &&
      props.checked
    );
  };

  // Render based on variant
  if (variant === "lobby") {
    const lobbyProps = props as LobbyProps;
    const { player, closed } = lobbyProps;

    return (
      <div className="h-full w-full absolute flex self-center">
        <div
          className={`h-full w-full relative flex flex-col items-center justify-center border-4 ${isChecked()
            ? "border-classic-border-secondary"
            : "border-transparent"
            }`}
          onClick={handleLobbyClick}
        >
          {player && player.name ? (
            <div
              className={`h-[55%] w-[55%] bg-white absolute opacity-100 z-[5] flex flex-col ${player.data?.vip
                ? "border border-classic-border-primary"
                : "border border-classic-border-secondary"
                }`}
            />
          ) : (
            <div className="h-[55%] w-[55%] bg-white absolute opacity-60" />
          )}

          <div className="w-full h-full max-h-24 max-w-24 z-10 flex justify-center items-center">
            {player && player.name ? (
              <img
                className="max-h-24 max-w-24 w-auto block relative z-10"
                style={{
                  animation: noAnimate
                    ? "none"
                    : "avatar-bounce 2s steps(1, start) infinite",
                  imageRendering: "pixelated",
                  margin: "auto",
                }}
                src={getAvatarPath()}
                alt={getAvatarPath()}
              />
            ) : null}
          </div>

          <div
            className={`h-[55%] w-[55%] absolute z-[1500] ${amHost() && player && player.id !== gameState.game?.host_id
              ? "cursor-pointer"
              : ""
              }`}
          >
            {player && player.data?.vip && player.status_str === GameStatus.GameStatusLobby && (
              <img
                className="h-1/2 w-auto"
                style={{ transform: "translate(-1rem, -1rem)" }}
                src={crown2}
                alt={crown2}
              />
            )}
          </div>

          <div className="absolute bottom-0 z-[5000]">
            {player && player.name ? (
              <div className="text-xl py-0.5 text-center text-white bg-black z-50 leading-none flex justify-center items-center px-1">
                <span>{player.name}</span>
                {player.data.premium && (
                  <span className="text-xs">&nbsp;⭐</span>
                )}
              </div>
            ) : (
              <div className="text-xl py-0.5 text-center text-white bg-black z-50 leading-none flex justify-center items-center px-1">
                {closed ? "CLOSED" : "OPEN"}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Profile variant (Avatar6-based)
  if (variant === "profile") {
    const profileProps = props as ProfileProps;
    const { bigText, premium } = profileProps;
    const avatarPaths = getResponsiveAvatarPaths();

    const getSizeClasses = () => {
      switch (size) {
        case "small":
          return {
            container: "h-[72px] w-[64px] sm:h-[104px] sm:w-[96px]",
            imageContainer: "h-[64px] w-[64px] sm:h-[96px] sm:w-[96px]",
          };
        case "large":
          return {
            container: "h-full w-full",
            imageContainer: "h-full w-full",
          };
        default: // medium
          return {
            container: "h-[200px] w-[192px]",
            imageContainer: "h-[192px] w-[192px]",
          };
      }
    };

    const sizeClasses = getSizeClasses();

    return (
      <div
        className={`relative flex flex-col items-center justify-start ${sizeClasses.container}`}
        onClick={handleBasicClick}
      >
        <div
          className={`relative flex flex-row items-center justify-center ${sizeClasses.imageContainer}`}
        >
          <div className="w-3/4 h-3/4 absolute bg-white opacity-100 border border-solid border-border-blue" />

          <img
            className={`inline-block ${size === "large" ? "max-w-full max-h-full w-auto h-auto" : ""} ${noAnimate ? "relative block" : "animate-avatar-bounce relative block z-10"
              } sm:hidden`}
            src={
              size === "small"
                ? avatarPaths.xsmall
                : size === "large"
                  ? avatarPaths.large
                  : avatarPaths.medium
            }
            alt="avatar"
          />
          <img
            className={`hidden ${size === "large" ? "max-w-full max-h-full w-auto h-auto" : ""} ${noAnimate ? "relative block" : "animate-avatar-bounce relative block z-10"
              } sm:inline-block`}
            src={
              size === "small"
                ? avatarPaths.small
                : size === "large"
                  ? avatarPaths.large
                  : avatarPaths.medium
            }
            alt="avatar"
          />
        </div>

        {name && (
          <div className="absolute bottom-0 z-30">
            <div
              className={`bg-opacity-60 bg-black break-words z-30 text-white leading-none px-1 text-center ${bigText ? "text-2xl" : "text-base"
                } ${premium ? "border-amber-500" : ""}`}
            >
              {name}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Basic variant (Avatar4-based)
  const avatarPath = getAvatarPath();

  return (
    <div
      className="text-base max-w-16 h-16 relative flex flex-col items-center justify-center"
      onClick={handleBasicClick}
    >
      <div className="shrink-0 text-base relative flex flex-row items-center justify-center">
        <div className="shrink-0 h-10 w-10 bg-white absolute opacity-100 border border-solid border-border-blue" />
        <div className="shrink-0 h-14 w-14 flex justify-center items-center">
          <img
            className={`max-h-14 max-w-14 w-auto relative block m-auto z-10 ${noAnimate ? "" : "animate-avatar-bounce"
              }`}
            src={avatarPath}
            alt={avatarPath}
          />
        </div>
      </div>

      {name && (
        <div className="absolute bottom-0 z-30">
          <div className="bg-black break-words z-30 text-white leading-none text-center bg-opacity-70">
            {name}
          </div>
        </div>
      )}
    </div>
  );
});

export default Avatar;
